<script lang="ts">
    import { type ComponentProps, onMount } from "svelte";
    import ComboBox from "./ComboBox.svelte";
    import NoResults from './NoResults.svelte';
    import Err from './Error.svelte'
    import Spinner from "./Spinner.svelte";
    import { rebounce } from "@square/svelte-store";
    import { decode } from 'he';

    export let comboboxNoResultsLabel: ComponentProps<ComboBox>['noResultsLabel']
    export let label = "Target Property"
    export let placeholder = "Search for a Location"
    let address: string | undefined
    export { address as term } // only expose term when they've picked a value
    export let intro: string
    export let mapHeader: string
    export let branchListHeader: string
    export let loListHeader: string
    export let noLosMessage: string
    export let noResultsMessage: string
    export let noResultsLinkText: string
    export let noResultsLinkHref: string
    export let noResultsLinkTarget: string
    // export let radius: number
    export let mapId: string
    export let backToBranchesLabel: string
    export let errorMsg: string
    export let rootFolder: string
    
    let mapEl: HTMLDivElement, map: google.maps.Map

    const BRANCHES_API_URL = `/api/losearch/searchbylocation`
    const LO_API_URL = `/api/losearch/searchbybranch`

    const distanceFormatter = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 1,
        style: 'unit',
        unit: 'mile',
        unitDisplay: 'long'
    })

    interface BranchResult {
        branchID: number
        address1: string
        address2: string
        latitude: number
        longitude: number
        city: string
        state: string
        zip: number
        distance: number
    }
    // Returns a string summary of a branch
    const summarize = (b: BranchResult) => {
        let addr = ''
        if (b.address1) addr = b.address1
        if (b.address2) addr += `, ${b.address2}`
        if (b.city) addr += `, ${b.city}`
        if (b.state || b.zip) addr += ', '
        if (b.state) addr += b.state
        if (b.zip) addr += ` ${b.zip}`
        return addr
    }

    interface LOResult {
        headshot: string
        name: string
        jobTitle?: string
        phone?: string
        email?: string
        link?: string | null
    }

    const autocompleteService = new google.maps.places.AutocompleteService()
    const search = async (input: string) => new Promise<google.maps.places.AutocompletePrediction[]>((res, rej) => {
        autocompleteService.getPlacePredictions({ input }, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                res(results)
            } else {
                // swallow errors
                // error('Could not get places autocomplete')
                // rej()
            }
        })
    })

    // Really feeling the lack of ErrorBoundary's in Svelte right now:
    let didError = false
    function error(msg: string) {
        didError = true
        console.error(msg) // just to be sure it gets logged
        return new Error(msg)
    }

    let placesService: google.maps.places.PlacesService, geocoder: google.maps.Geocoder
    onMount(async () => {
        map = new google.maps.Map(mapEl, {
            mapId,
            streetViewControl: false,
            maxZoom: 14 // prevent zooming in too far on a single result
        })
        geocoder = new google.maps.Geocoder()
        placesService = new google.maps.places.PlacesService(map)
    })

    let comboboxEl: HTMLDivElement | undefined
    const io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
            getCurrentPosition()
            io.unobserve(entry.target)
        }
    })
    $: if (comboboxEl) io.observe(comboboxEl)

    let value: google.maps.places.AutocompletePrediction | google.maps.GeocoderResult
    const needsDetails = (val: typeof value): val is google.maps.places.AutocompletePrediction =>
        (val as google.maps.GeocoderResult).geometry === undefined
    let center: google.maps.LatLng

    let gettingPosition = false
    async function getCurrentPosition() {
        gettingPosition = true
        try {
            const { coords } = await new Promise<GeolocationPosition>((res, rej) => {
                navigator.geolocation.getCurrentPosition(res, rej)
            })
            const { results } = await geocoder.geocode({ location: {
                lat: coords.latitude,
                lng: coords.longitude
            }})
            if (results[0]) value = results[0]
        } catch {/* swallow errors */} finally {
            gettingPosition = false
        }
    }

    let activeBranchIndex: number | null | undefined, focusedBranchIndex: number | undefined
    $: activeBranch = activeBranchIndex == null ? undefined : branches[activeBranchIndex]
    $: if (activeBranchIndex != null) focusedBranchIndex = activeBranchIndex

    // Update branches when value changes. Can't use a promise because we need the map
    // to stay visible even if it's loading new ones
    let branches: BranchResult[], loading = false
    const getBranches = rebounce(async () => {
        // First get the place details
        const center = (!needsDetails(value) ? value
            : await new Promise<google.maps.places.PlaceResult>((res, rej) => {
                placesService.getDetails({
                    placeId: value.place_id,
                    fields: ['geometry.location']
                }, (result, status) => {
                    if (result == null || status !== google.maps.places.PlacesServiceStatus.OK) {
                        loading = false
                        throw error('Could not retrieve place details')
                    }
                    res(result)
                })
            })
        ).geometry?.location
        if (!center) throw error('Could not get center')

        // then the list of branches using the center lat/lng
        try {
            const res = await fetch(`${BRANCHES_API_URL}?rootFolder=${rootFolder}&lat=${center?.lat()}&lng=${center?.lng()}`)
            if (!res.ok) throw error('Could not fetch branches')
            return { center, branches: await res.json() as BranchResult[] }
        } finally {
            loading = false
        }
    })
    async function handleValueChange() {
        loading = true
        activeBranchIndex = null
        address = needsDetails(value) ? value.description : value.formatted_address
        ;({ center, branches } = await getBranches())
        // Preselect the first branch on desktop
        if (window.innerWidth > 768) activeBranchIndex = 0
    }
    $: if (value) handleValueChange()

    // Keep map up to date with branches
    let markers: google.maps.marker.AdvancedMarkerElement[] = [], centerMarker: google.maps.Marker
    $: if (branches?.length && map && center) {
        // Clear old markers
        markers.forEach(marker => marker.map = null)
        
        const newBounds = new google.maps.LatLngBounds()

        // Draw or update center marker
        if (!centerMarker) {
            centerMarker ??= new google.maps.Marker({
                position: center,
                map,
                zIndex: 1
            })
        } else {
            centerMarker.setPosition(center)
        }
        newBounds.extend(center)
        
        // Draw new markers and fit map to them
        markers = branches.map(({ latitude: lat, longitude: lng }, i) => {
            newBounds.extend({ lat, lng })

            const pin = document.createElement('div')
            pin.className = 'pin'
            pin.textContent = i+1+''
            const marker = new google.maps.marker.AdvancedMarkerElement({
                position: { lat, lng },
                map,
                content: pin
            })
            marker.addListener('click', () => {
                activeBranchIndex = i
            })
            return marker
        })
        // https://stackoverflow.com/questions/3873195/calling-map-fitbounds-multiple-times-in-google-maps-api-v3-0
        // and https://groups.google.com/g/google-maps-js-api-v3/c/fN7JWikYn3w?pli=1#d4a366f6dcb910ec
        setTimeout(() => map.fitBounds(newBounds), 100)
    }
    // Keep marker styles up-to-date
    $: markers?.forEach((marker, i) => {
        const active = i == activeBranchIndex
        marker.zIndex = active ? 10 : 5
        ;(marker.content as HTMLDivElement).classList[active ? 'add' : 'remove']('active')
    })
    // Keep active branch in view
    const branchEls: HTMLButtonElement[] = []
    $: if (activeBranchIndex != null) branchEls[activeBranchIndex]?.scrollIntoView({
        // https://stackoverflow.com/a/52835382
        behavior: 'smooth', block: 'nearest', inline: 'start'
    })

    // Load LOs when activeBranch changes
    let losReq: Promise<LOResult[]>
    const fetchLOs = rebounce(async () => {
        const res = await fetch(`${LO_API_URL}?branchid=${activeBranch?.branchID}`)
        if (!res.ok) throw error(`Couldn't fetch LOs for branch ${activeBranch?.branchID}`)
        return await res.json() as LOResult[]
    })
    $: if (activeBranch) losReq = fetchLOs()

    // Tabbing behavior through branches
    function handleKeydownOnBranch(e: KeyboardEvent, i: number) {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            branchEls[i+1]?.focus()
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            if (i > 0) branchEls[i-1].focus()
        } else if (e.key === 'Home') {
            e.preventDefault()
            branchEls[0].focus()
        } else if (e.key === 'End') {
            e.preventDefault()
            branchEls[branchEls.length - 1].focus()
        }
    }
</script>

{#if didError}
    <Err>{@html decode(errorMsg)}</Err>
{:else}
<ComboBox
    {label}
    {placeholder}
    {search}
    bind:el={comboboxEl}
    disabled={gettingPosition}
    disabledValue={"Getting current location..."}
    noResultsLabel={comboboxNoResultsLabel}
    bind:value
    itemToString={(i) => needsDetails(i) ? i.description : i.formatted_address}
    itemToHTML={(i) => {
        if (!needsDetails(i)) return i.formatted_address

        let html = ''
        if (i.structured_formatting.main_text_matched_substrings?.length) {
            html = 
                // Make sure you reduce from the right
                i.structured_formatting.main_text_matched_substrings.reduceRight((chars, substring) => {
                    chars.splice(substring.offset, substring.length, ...`<mark>${i.structured_formatting.main_text.substring(substring.offset, substring.offset + substring.length)}</mark>`.split(''))
                    return chars
                }, i.structured_formatting.main_text.split('')).join('')
        } else {
            html = i.structured_formatting.main_text
        }
        if (i.structured_formatting.secondary_text) {
            html += ` <span>${i.structured_formatting.secondary_text}</span>`
        }
        return html
    }}
/>
<!-- This has to be hidden, not wrapped in {#if} because we need the map on the page for GMaps APIs -->
<div hidden={!branches?.length}>
    <div class="my-8 rich-text text-xl max-w-none">{@html decode(intro)}</div>
    <div class="flex items-stretch md:h-[40rem] relative gap-4">
        <div class="max-md:hidden flex flex-col items-stretch flex-1">
            <h3 class="font-bold font-serif mb-3 text-2xl">{mapHeader}</h3>
            <div class="flex-1 mt-0.5" data-map bind:this={mapEl}></div>
        </div>
        <div class="flex flex-col items-stretch flex-1 {activeBranch ? 'max-md:hidden' : ''}">
            <h3 class="font-bold font-serif mb-3 text-2xl" id="branches-header">{branchListHeader}</h3>
            <ul class="flex-1 pt-0.5 px-1.5 pb-1.5 flex flex-col items-stretch gap-6 overflow-y-auto scroll-smooth" role="tree" aria-labelledby="branches-header">
                {#if branches?.length}
                    {#each branches as branch, i}
                        <li role="none">
                            <button
                                class="bg-light transition duration-100 ease-linear hover:bg-stone rounded-xl shadow-md text-gray-700 p-4 flex items-start gap-4 w-full aria-selected:bg-[#E7F2F4]"
                                bind:this={branchEls[i]}
                                on:focus={() => focusedBranchIndex = i}
                                role="treeitem"
                                on:keydown={(e) => handleKeydownOnBranch(e, i)}
                                tabindex={focusedBranchIndex === i || focusedBranchIndex == null && i === 0 ? undefined : -1}
                                on:click={() => { activeBranchIndex = i }} aria-selected={i === activeBranchIndex}
                                aria-label="Branch {i+1} is {distanceFormatter.format(branch.distance)} away, located at {summarize(branch)}"
                            >
                                <div class="flex-1">
                                    <span class="block text-sm font-semibold uppercase mb-0.5 text-gray-700">Address</span>
                                    {summarize(branch)}
                                </div>
                                <div class="text-sm text-center whitespace-nowrap">
                                    <div class="marker max-md:hidden">
                                        <div class="pin">{i+1}</div>                           
                                    </div>
                                    <span class="text-gray-700 font-semibold">{distanceFormatter.format(branch.distance)}</span>
                                </div>
                            </button>
                        </li>
                    {/each}
                {/if}
            </ul>
        </div>
        {#if loading}
            <div class="flex items-center justify-center size-full absolute inset-0 bg-light/80 z-30"><Spinner /></div>
        {/if}
        {#if activeBranch && losReq}
            <div class="flex flex-col items-stretch flex-[1.05]">
                <header>
                    {#if activeBranch}
                        <div class="md:hidden mb-4">
                            <button class="link inline-flex items-center gap-2 mb-4 font-semibold" on:click={() => { activeBranchIndex = null }}>
                                <div class="rounded-full aspect-square inline-flex items-center px-2 justify-center border border-current">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5" fill="currentColor" viewBox="0 0 448 512"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>
                                </div>
                                {backToBranchesLabel}
                            </button>
                            <p>{summarize(activeBranch)}</p>
                        </div>
                    {/if}
                    <h3 class="font-bold font-serif mb-3 text-2xl">{loListHeader}</h3>
                </header>
                {#await losReq}
                    <div class="flex items-center justify-center size-full">
                        <Spinner />
                    </div>
                {:then los}
                    {#if los?.length}
                        <ul class="flex-1 pt-0.5 px-1.5 pb-1.5 flex flex-col items-stretch gap-6 overflow-y-auto scroll-smooth">
                            {#each los as lo}
                                <li class="gap-1 p-4 rounded-xl shadow-md flex flex-col justify-start items-center">
                                    <img loading="lazy" src={lo.headshot} class="size-20 rounded-full object-cover object-top" alt={lo.name} />
                                    <span class="block text-xl/tight mt-1 font-medium tracking-snug text-center">
                                        {lo.name}
                                    </span>
                                    {#if lo.jobTitle}
                                        <span class="block text-sm mt-0.5 font-semibold uppercase text-center text-gray-600">{lo.jobTitle}</span>
                                    {/if}
                                    <div class="flex mt-3 gap-5 items-center justify-center">
                                        {#if lo.phone}
                                            <a aria-label="Call {lo.name} at {lo.phone}" href="tel:{lo.phone}" class="btn btn-wrap btn-base btn-twilight">
                                                <svg role="presentation" class="icon icon-phone relative -top-[0.05em]" height="20" width="20">
                                                    <use href="#icon-phone" xmlns:xlink="http://www.w3.org/1999/xlink"></use>
                                                </svg>
                                                Call
                                            </a>
                                        {/if}
                                        {#if lo.email}
                                            <a aria-label="email {lo.name} at {lo.email}" href="mailto:{lo.email}" class="btn btn-wrap btn-base btn-twilight-outline">
                                                <svg role="presentation" class="icon icon-email relative -top-[0.05em]" height="20" width="20">
                                                    <use href="#icon-email" xmlns:xlink="http://www.w3.org/1999/xlink"></use>
                                                </svg>
                                                Email
                                            </a>
                                        {/if}
                                        {#if lo.link}
                                            <a class="link font-semibold text-center" href={lo.link} aria-label="Visit {lo.name}'s Profile">See Profile</a>
                                        {/if}
                                    </div>
                                </li>
                            {/each}
                        </ul>
                    {:else}
                        <NoResults>
                            <p>{noLosMessage}</p>
                        </NoResults>
                    {/if}
                {/await}
            </div>
        {/if}
    </div>
</div>
{#if branches && !branches?.length}
    <NoResults margin="mt-4">
        {@html decode(noResultsMessage)}
        {#if noResultsLinkHref && noResultsLinkText}
            <a class="btn btn-base btn-twilight" href={noResultsLinkHref} target={noResultsLinkTarget}>{noResultsLinkText}</a>
        {/if}
    </NoResults>
{/if}
{/if}

<style>
    .marker {
        width: min-content;
        padding: 0.25rem; /* this can't be percentage because then it can't compute the layout properly */
        aspect-ratio: 31.7 / 37.5;
        margin: 0 auto;
        position: relative;
    }
        /* Draw the marker shape */
        .marker::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            aspect-ratio: 1;
            border: 2px solid #111;
            border-radius: 50% 50% 50% 0%;
            transform: rotate(-45deg);
        }

        .pin, div :global(.pin) {
            border-radius: 100%;
            background: #297489;
            color: #fff;
            display: flex;
            aspect-ratio: 1;
            align-items: center;
            justify-content: center;
            min-width: 1.5rem;
            font-weight: 700;
            padding: 0 0.1875rem;
        }

        [data-map] :global(.pin) {
            font-size: 16px;
            width: min-content;
            min-width: 32px;
        }

            div :global(.pin.active), [aria-selected="true"] .pin {
                background: #00495F;
            }
</style>